import request from './request'
import { getMockScheduleMetaResponse, shouldUseScheduleMock } from './scheduleMetaMock'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface ScheduleMetaOption {
  value: string
  label: string
}

export interface ScheduleLegendMeta {
  value: string
  label: string
  color_key?: string
}

export interface ScheduleTechnicianMeta {
  value: string | number
  label: string
}

export interface ScheduleMetaData {
  categories?: ScheduleMetaOption[]
  technicians?: ScheduleTechnicianMeta[]
  legend?: ScheduleLegendMeta[]
  slot_start?: string
  slot_end?: string
  slot_step_minutes?: number
  [key: string]: unknown
}

export interface ScheduleLegendItem {
  key: string
  label: string
  color: string
}

export interface ScheduleTechnicianView {
  id: string
  name: string
  role: string
}

export interface ScheduleSlotConfig {
  slotStart: string
  slotEnd: string
  slotStepMinutes: number
  dayStartMinutes: number
}

export const DEFAULT_SCHEDULE_CATEGORIES: ScheduleMetaOption[] = [
  { value: 'all', label: 'All' },
  { value: 'essential_care', label: 'Essential Care' },
  { value: 'enhanced_care', label: 'Enhanced Care' },
  { value: 'premium_care', label: 'Premium Care' },
  { value: 'follow_up_care', label: 'Follow-up Care' },
  { value: 'add_on_service', label: 'Add-on Service' },
]

export const DEFAULT_SCHEDULE_TECHNICIANS: ScheduleTechnicianView[] = []

export const DEFAULT_SCHEDULE_LEGEND: ScheduleLegendItem[] = [
  { key: 'essential_care', label: 'Essential Care', color: '#ddeeff' },
  { key: 'enhanced_care', label: 'Enhanced Care', color: '#dff4c8' },
  { key: 'premium_care', label: 'Premium Care', color: '#ffefc8' },
  { key: 'follow_up_care', label: 'Follow-up Care', color: '#ffebeb' },
  { key: 'add_on_service', label: 'Add-on Service', color: '#98d5be' },
]

export const DEFAULT_SCHEDULE_SLOT_CONFIG: ScheduleSlotConfig = {
  slotStart: '08:00',
  slotEnd: '20:00',
  slotStepMinutes: 30,
  dayStartMinutes: 8 * 60,
}

const LEGEND_COLORS: Record<string, string> = {
  essential_care: '#ddeeff',
  enhanced_care: '#dff4c8',
  premium_care: '#ffefc8',
  follow_up_care: '#ffebeb',
  add_on_service: '#98d5be',
  essential: '#ddeeff',
  enhanced: '#dff4c8',
  premium: '#ffefc8',
  followup: '#ffebeb',
  addon: '#98d5be',
}

const CATEGORY_CLASS_ALIAS: Record<string, string> = {
  essential_care: 'essential',
  enhanced_care: 'enhanced',
  premium_care: 'premium',
  follow_up_care: 'followup',
  add_on_service: 'addon',
}

function normalizeOption(
  entry: ScheduleMetaOption | ScheduleTechnicianMeta,
  fallbacks: ScheduleMetaOption[],
): ScheduleMetaOption | null {
  const value = String(entry.value ?? '').trim()
  const label = String(entry.label ?? '').trim()
  if (!value && value !== '0') return null
  if (!label && value === 'all') return { value: 'all', label: 'All' }
  if (!label) {
    const fallback = fallbacks.find((item) => item.value === value)
    return { value, label: fallback?.label ?? value }
  }
  return { value, label }
}

function buildCategoryOptions(raw: ScheduleMetaOption[] | undefined): ScheduleMetaOption[] {
  if (!Array.isArray(raw) || !raw.length) return [...DEFAULT_SCHEDULE_CATEGORIES]
  const options = raw
    .map((entry) => normalizeOption(entry, DEFAULT_SCHEDULE_CATEGORIES))
    .filter((item): item is ScheduleMetaOption => item != null)
  return options.length ? options : [...DEFAULT_SCHEDULE_CATEGORIES]
}

function parseTechnicianLabel(label: string): { name: string; role: string } {
  const match = label.match(/^(.+?)\s*\(([^)]+)\)\s*$/i)
  if (match?.[1] && match[2]) {
    return { name: match[1].trim(), role: match[2].trim() }
  }
  return { name: label.trim(), role: '' }
}

export function buildTechnicianOptions(
  raw: ScheduleTechnicianMeta[] | undefined,
): { options: ScheduleMetaOption[]; technicians: ScheduleTechnicianView[] } {
  if (!Array.isArray(raw) || !raw.length) {
    return { options: [{ value: 'all', label: 'All' }], technicians: [] }
  }

  const options: ScheduleMetaOption[] = []
  const technicians: ScheduleTechnicianView[] = []

  for (const entry of raw) {
    const rawValue = entry.value
    const isAll = rawValue === 0 || rawValue === '0' || rawValue === 'all'
    const value = isAll ? 'all' : String(rawValue ?? '').trim()
    const label = String(entry.label ?? '').trim()
    if (!label) continue

    options.push({ value: value || 'all', label })

    if (isAll) continue

    const parsed = parseTechnicianLabel(label)
    technicians.push({
      id: value,
      name: parsed.name,
      role: parsed.role,
    })
  }

  if (!options.length) {
    return { options: [{ value: 'all', label: 'All' }], technicians: [] }
  }

  return { options, technicians }
}

export function buildLegendItems(raw: ScheduleLegendMeta[] | undefined): ScheduleLegendItem[] {
  if (!Array.isArray(raw) || !raw.length) return [...DEFAULT_SCHEDULE_LEGEND]

  const items = raw
    .map((entry) => {
      const key = (entry.color_key ?? entry.value ?? '').trim()
      const label = (entry.label ?? '').trim()
      if (!key || !label) return null
      return {
        key,
        label,
        color: LEGEND_COLORS[key] ?? '#ddeeff',
      }
    })
    .filter((item): item is ScheduleLegendItem => item != null)

  return items.length ? items : [...DEFAULT_SCHEDULE_LEGEND]
}

function parseTimeToMinutes(value: string): number | null {
  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!match?.[1] || !match[2]) return null
  return Number(match[1]) * 60 + Number(match[2])
}

export function buildScheduleSlotConfig(meta: ScheduleMetaData | null | undefined): ScheduleSlotConfig {
  const slotStart = meta?.slot_start?.trim() || DEFAULT_SCHEDULE_SLOT_CONFIG.slotStart
  const slotEnd = meta?.slot_end?.trim() || DEFAULT_SCHEDULE_SLOT_CONFIG.slotEnd
  const slotStepMinutes =
    typeof meta?.slot_step_minutes === 'number' && meta.slot_step_minutes > 0
      ? meta.slot_step_minutes
      : DEFAULT_SCHEDULE_SLOT_CONFIG.slotStepMinutes
  const dayStartMinutes = parseTimeToMinutes(slotStart) ?? DEFAULT_SCHEDULE_SLOT_CONFIG.dayStartMinutes

  return {
    slotStart,
    slotEnd,
    slotStepMinutes,
    dayStartMinutes,
  }
}

export function buildTimeSlotsFromConfig(config: ScheduleSlotConfig): string[] {
  const startMinutes = parseTimeToMinutes(config.slotStart)
  const endMinutes = parseTimeToMinutes(config.slotEnd)
  if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
    return buildTimeSlotsFromConfig(DEFAULT_SCHEDULE_SLOT_CONFIG)
  }

  const slots: string[] = []
  // 含 slotEnd（默认 20:00 → 显示到 08:00 PM），与网格 --schedule-slot-count: 25 一致
  for (let minute = startMinutes; minute <= endMinutes; minute += config.slotStepMinutes) {
    const hour = Math.floor(minute / 60)
    const part = minute % 60
    const date = new Date(2000, 0, 1, hour, part)
    slots.push(
      date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }),
    )
  }
  return slots
}

export function mapCategoryToBlockClass(category: string): string {
  return CATEGORY_CLASS_ALIAS[category] ?? category.replace(/_/g, '-')
}

export interface ScheduleMetaView {
  categories: ScheduleMetaOption[]
  technicianOptions: ScheduleMetaOption[]
  technicians: ScheduleTechnicianView[]
  legend: ScheduleLegendItem[]
  slotConfig: ScheduleSlotConfig
}

export function mapScheduleMetaToView(meta: ScheduleMetaData | null | undefined): ScheduleMetaView {
  const technicianData = buildTechnicianOptions(meta?.technicians)
  return {
    categories: buildCategoryOptions(meta?.categories),
    technicianOptions: technicianData.options,
    technicians: technicianData.technicians,
    legend: buildLegendItems(meta?.legend),
    slotConfig: buildScheduleSlotConfig(meta),
  }
}

/** POST `/api/admin/schedule/meta` */
export async function postScheduleMeta(): Promise<ApiResponse<ScheduleMetaData>> {
  if (shouldUseScheduleMock()) {
    return getMockScheduleMetaResponse() as ApiResponse<ScheduleMetaData>
  }
  return request.post<ApiResponse<ScheduleMetaData>>('/api/admin/schedule/meta', {})
}
